import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  SupplierInboxJobsService,
  SupplierJobInteractionService,
  SupplierJobStoreService,
  UserService,
} from '../../../services';
import { ISupplierInboxJob } from '../../../interfaces/inbox-job';
import { MapControlTask } from '../../../components/map-control/map-control.component';
import { SupplierInboxType } from 'src/app/enums/supplier-inbox.type.enum';
import { Subscription } from 'rxjs-compat';
import { JobStatus } from '../../../enums/job-status';
import { IRouteRecommendation } from '../../../interfaces/route-recommendations/route-recommendation';
import { RouteRecommendationService } from '../../../services/route-recommendation.service';
import {
  AnalyticsJobComponent,
  JobAnalyticsService,
} from '../../../services/product-analytics/job-analytics.service';

@Component({
  selector: 'supplier-jobs-inbox-item',
  styleUrls: ['supplier-jobs-inbox-item.component.scss'],
  templateUrl: './supplier-jobs-inbox-item.component.html',
})
export class SupplierJobsInboxItemComponent implements OnInit, AfterViewInit, OnDestroy {
  hidingJob = false;
  private _job: ISupplierInboxJob = null;

  protected readonly SupplierInboxType = SupplierInboxType;

  @Input() currentOrgId: number;
  @Input() currentOrgTypeId: number;

  @Input() inboxType: number;

  @Output() handBackJobEvent: EventEmitter<number> = new EventEmitter<number>();

  onHandBackClick = () => this.handBackJobEvent.emit(this.job.id);

  allocateTruckClick = () => this.supplierJobStoreService.setAllocateTruckDialogVisible$(true);

  allocateDepotClick = () => this.supplierJobStoreService.setAllocateDepotDialogVisible$(true);

  allocateDriverClick = () => this.supplierJobStoreService.setAllocateDriverDialogVisible$(true);

  chosenTaskId: number;
  mapSize = '280x320';
  tasks: MapControlTask[];
  protected selectedRoute: IRouteRecommendation;
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setMapSize();
  }

  setMapSize() {
    if (window.innerWidth > 1400 || window.innerWidth <= 730) {
      this.mapSize = '480x320';
    } else {
      this.mapSize = '280x320';
    }
  }

  @Input('job')
  public set job(x: ISupplierInboxJob) {
    this._job = x;
    this.tasks = x.tasks;
    this.tasks.forEach((task) => {
      task.map = {};
    });
    this.highlightedTaskId = x.tasks[0]?.id;
  }

  public get job() {
    return this._job;
  }

  onShowPriceDetails($event: MouseEvent) {
    if (this.showPriceDetailsEnabled) {
      this.supplierJobStoreService.goToPriceDetails(this.job.id, $event);
    }
  }

  get showPriceDetailsEnabled(): boolean {
    return (
      this.inboxType == SupplierInboxType.ExistingClosed ||
      this.inboxType == SupplierInboxType.ExistingCancelled
    );
  }

  get showWatcherIndicator(): boolean {
    return (
      this.job.supplierWatchCount > 0 &&
      [
        SupplierInboxType.InboxMatched,
        SupplierInboxType.InboxBidWatch,
        SupplierInboxType.InboxRejected,
        SupplierInboxType.InboxDirect,
        SupplierInboxType.InboxJobAlert,
      ].includes(this.inboxType)
    );
  }

  showSupplierPremiums = false;

  get supplierPremiumTotal(): number {
    return (this.job?.supplierPremiums ?? []).reduce(
      (acc, p) => acc + Math.round(p.premium * 100) / 100,
      0,
    );
  }

  subscriptions: Subscription[] = [];

  constructor(
    private supplierInboxJobService: SupplierInboxJobsService,
    private supplierInteractionService: SupplierJobInteractionService,
    public userService: UserService,
    public supplierJobStoreService: SupplierJobStoreService,
    private routeRecommendationService: RouteRecommendationService,
    private jobAnalyticsService: JobAnalyticsService,
  ) {
    this.setMapSize();

    const sub = this.supplierJobStoreService.selectedJob$.subscribe((job) => {
      this.selectedJob = job;
    });

    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  ngAfterViewInit(): void {}

  ngOnInit(): void {}

  viewJobDetail($event: any) {
    this.supplierJobStoreService.goToJobDetails(this.job.id, $event);
  }

  viewRouteDetail($event: any) {
    this.supplierJobStoreService.goToRouteDetails(this.job.routeRecommendationId, $event);
  }

  async watchJob(e) {
    e.stopPropagation();
    this.supplierInboxJobService.watchJob(this.job.id).then(() => {
      this.supplierInteractionService.logSingleJobInteraction('WatchJobSuccess', this.job.id);
    });

    if (!this.job.supplierWatching) {
      this.job.supplierWatchCount = (this.job.supplierWatchCount ?? 0) + 1;
    }
    this.job.supplierWatching = true;
  }

  unWatchJob() {
    this.supplierJobStoreService.unWatchJob(this.job.id);
  }

  hideJob() {
    this.supplierJobStoreService.setHideJobDialogVisible(true);
  }

  unHideJob() {
    this.hidingJob = true;
    this.supplierJobStoreService.unHideJob(this.job.id).then(() => {
      this.supplierJobStoreService.removeJob(this.job.id);
      this.hidingJob = false;
    });
  }

  grabJobClick() {
    this.supplierJobStoreService.selectJob(this.job);
    this.supplierJobStoreService.setAcceptJobDialogVisible(true);
    this.jobAnalyticsService.sendGrabNavigationEvent('Click grab from job card');
    this.jobAnalyticsService.sendGrabJobEvent(this.getAnalyticsJobComponent());
  }

  private getAnalyticsJobComponent(): AnalyticsJobComponent {
    return {
      jobNumber: this.job.jobNumber,
      journeyLeg: this.tasks.length,
      referrerPage: 'supplierJobsInbox',
      tasks: this.tasks,
      totalEarnings: this.job.total,
    } as AnalyticsJobComponent;
  }

  highlightedTaskId: number;

  showMapForTaskId = null;
  isGridView: boolean = false;
  taskMode: boolean = false;

  selectedJob: ISupplierInboxJob;
  showRouteItineraryPopup: boolean = false;

  get showEditScheduleButton() {
    return (
      !this.job.isFromRouteRecommendation &&
      (this.inboxType == SupplierInboxType.ExistingUnallocated ||
        this.inboxType == SupplierInboxType.ExistingWaiting)
    );
  }

  totalDistance(job: ISupplierInboxJob) {
    const totalDistance = job.tasks.reduce((total, task) => {
      return total + task.distance;
    }, 0);

    return totalDistance;
  }

  selectJob(job: ISupplierInboxJob) {
    this.supplierJobStoreService.selectJob(job);
  }

  setHighlighted(e, id: number) {
    e.stopPropagation();

    this.highlightedTaskId = id;

    if (this.chosenTaskId == id) {
      this.taskMode = !this.taskMode;
      this.chosenTaskId = null; // Distinct from highlighted
    } else {
      this.chosenTaskId = id; // Distinct from highlighted
      this.taskMode = true;
    }

    this.jobAnalyticsService.sendTaskSwitchEvent(id, this.job.jobNumber);
  }

  toggleMap(event: MouseEvent, taskId: number) {
    this.showMapForTaskId = this.showMapForTaskId == taskId ? null : taskId;
  }

  handBackDisabledReason() {
    if (this.job.boughtUserId != this.userService.currentUser?.id) {
      return 'You can only hand back jobs which you grabbed';
    }

    if (new Date() > new Date(this.job.latestHandBackDateTime)) {
      return 'Hand back cooling off period has expired';
    }

    return null;
  }

  async openRouteItineraryPopup() {
    this.selectedRoute = await this.routeRecommendationService.get(this.job.routeRecommendationId);

    this.showRouteItineraryPopup = true;
  }

  getEditScheduleButtonLabel() {
    let scheduleMissing: boolean = this.job.tasks.some(
      (x) =>
        x.taskSupplierSchedule?.specifiedPickUpTime == null ||
        x.taskSupplierSchedule?.specifiedDropOffTime == null,
    );

    if (scheduleMissing) {
      return 'Add Schedule';
    }

    return 'Edit Schedule';
  }

  protected editSchedule() {
    this.supplierJobStoreService.selectJob(this.job);
    this.supplierJobStoreService.setEditScheduleDialogVisible(true);
  }
}
